import { useState } from 'react';
import { Button } from '@givebutter/primitives/button';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { show as showIntercomChat } from '@intercom/messenger-js-sdk';
import { widgetStrategies } from 'components/pages/campaign-settings/campaign-sharing/widgets/widgetStrategies';
import { WidgetInstructions } from 'components/widgets/WidgetInstructions';
import { WIDGETS_GUIDE_LINK } from 'constants/links';
import { WidgetPlatform } from 'constants/widgets';
import { useLocalStorage } from 'contexts/useLocalStorage';

const WidgetLibraryInstallation = (props: { inPortal: boolean }) => {
    const { inPortal } = props;
    const [showInstallationInstructions, setShowInstallationInstructions] = useState(false);
    const { selectedWidgetPlatform, setSelectedWidgetPlatform } = useLocalStorage(state => {
        return {
            selectedWidgetPlatform: state.selectedWidgetPlatform,
            setSelectedWidgetPlatform: state.setSelectedWidgetPlatform,
        };
    });

    const selectedPlatform = widgetStrategies.find(
        platform => platform.platform === selectedWidgetPlatform,
    );

    return showInstallationInstructions ? (
        <div className="bg-gray-25 flex flex-col gap-4 rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between">
                {selectedPlatform?.platform === WidgetPlatform.other ? (
                    <div className="font-medium">How to install widgets on your website</div>
                ) : (
                    <img
                        className="max-h-6"
                        alt={selectedPlatform?.platform}
                        src={`${selectedPlatform?.image ?? ''}`}
                    />
                )}
                <Button
                    variant="transparent"
                    shape={'square'}
                    onClick={() => setShowInstallationInstructions(false)}
                >
                    <FontAwesomeIcon icon={faClose} />
                </Button>
            </div>
            <div className="flex flex-col gap-3">
                <WidgetInstructions
                    inPortal={inPortal}
                    platform={selectedPlatform?.platform ?? WidgetPlatform.wordpress}
                    steps={selectedPlatform?.platformSetup ?? []}
                    setupType="library"
                    widget={null}
                />
            </div>

            {selectedPlatform?.platformSetupVideoEmbedSrc !== undefined && (
                <div className="relative h-0 pb-[62.5%]">
                    <iframe
                        src={selectedPlatform?.platformSetupVideoEmbedSrc}
                        className="absolute left-0 top-0 h-full w-full"
                        title={`How to install widgets library on ${selectedPlatform?.name}`}
                        allowFullScreen
                    ></iframe>
                </div>
            )}

            <div className="text-sm">
                <div className="font-medium">Still need help?</div>
                <a
                    target="_blank"
                    rel="noreferrer"
                    href={selectedPlatform?.helpCenterArticleUrl ?? WIDGETS_GUIDE_LINK}
                >
                    Read our full guide
                </a>{' '}
                or{' '}
                <Button
                    variant="none"
                    color="primary"
                    className="font-medium"
                    onClick={() => showIntercomChat()}
                >
                    chat with our support team
                </Button>
                .
            </div>
        </div>
    ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
            {widgetStrategies.map(strat => {
                const isOtherPlatform = strat.platform === WidgetPlatform.other;
                return (
                    <Button
                        variant="transparent"
                        color="neutral"
                        key={strat.name}
                        className={`p-0 ${
                            isOtherPlatform ? 'h-20 sm:col-span-2 md:col-span-3' : 'h-24'
                        }`}
                        onClick={() => {
                            setSelectedWidgetPlatform(strat.platform);
                            setShowInstallationInstructions(true);
                        }}
                    >
                        <div className="bg-gray-25 flex h-full w-full items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100">
                            <div className={`${isOtherPlatform ? 'p-4' : 'p-8'}`}>
                                {isOtherPlatform ? (
                                    <div className="font-medium">I use another platform</div>
                                ) : (
                                    <img
                                        className="max-h-[2rem]"
                                        alt={strat.platform}
                                        src={`${strat.image ?? ''}`}
                                    />
                                )}
                            </div>
                        </div>
                    </Button>
                );
            })}
        </div>
    );
};

export { WidgetLibraryInstallation };
